/**
 * Canonical driver-registration resume decision (MR !388 finding 7 / blocker C).
 *
 * Given the authoritative `GET /api/users/me` resume contract, decide what the
 * `register/driver` screen should do when it opens with an existing session
 * token. Pure function so the routing can be unit-tested without a renderer.
 *
 * FAIL-CLOSED contract:
 *   - resume ONLY when resume_eligible===true AND state==='partial' AND a valid
 *     non-empty server_identity.email is present.
 *   - state='owner'  -> clear token, redirect /onboarding/owner
 *   - state='complete' -> clear token, redirect /dashboard/home
 *   - missing/malformed me, non-2xx path (caller passes null), missing email,
 *     or any other state -> block (login), NEVER fresh registration with token.
 *
 * @param me  the parsed `users/me` response (or null if the token is untrusted).
 */
export type ResumeMode = 'register' | 'resume' | 'profile_recovery' | 'redirect' | 'block';

export const PROFILE_RECOVERY_FIELDS = [
  'nif',
  'address',
  'license_number',
  'date_of_birth',
] as const;

export interface ResumeDecision {
  mode: ResumeMode;
  serverEmail: string | null;
  redirect: string | null;
  emailMismatch: boolean;
  resumeEligible: boolean;
  missingProfileFields: string[];
}

function hasValidServerEmail(me: {
  server_identity?: { email?: string } | null;
} | null): string | null {
  const raw = me?.server_identity?.email;
  if (typeof raw !== 'string') return null;
  const email = raw.trim();
  if (!email || !email.includes('@')) return null;
  return email;
}

export function resolveResumeDecision(me: {
  resume_eligible?: boolean;
  state?: string;
  server_identity?: { email?: string; email_verified?: boolean } | null;
  email_mismatch?: boolean;
  profile_recovery?: {
    eligible?: boolean;
    missing_fields?: unknown;
    reason?: string;
  } | null;
} | null): ResumeDecision {
  const emailMismatch = Boolean(me?.email_mismatch);
  const serverEmail = hasValidServerEmail(me);
  const rawMissingFields = me?.profile_recovery?.missing_fields;
  const missingProfileFields = Array.isArray(rawMissingFields)
    && rawMissingFields.length > 0
    && rawMissingFields.every(
      (field): field is (typeof PROFILE_RECOVERY_FIELDS)[number] =>
        typeof field === 'string'
        && (PROFILE_RECOVERY_FIELDS as readonly string[]).includes(field),
    )
    ? [...rawMissingFields]
    : [];

  // No trusted identity (token missing / server rejected it / malformed) ->
  // BLOCK, never fall into fresh registration while a token may still linger.
  // The caller is responsible for quarantining the token and routing to login.
  if (!me) {
    return {
      mode: 'block',
      serverEmail: null,
      redirect: '/login',
      emailMismatch: false,
      resumeEligible: false,
      missingProfileFields: [],
    };
  }

  // Owner (including dual-role Owner+Driver) and already-complete drivers are
  // NOT driver resumes. Clear the cached token and route to the right funnel.
  if (me.state === 'owner') {
    return {
      mode: 'redirect',
      serverEmail,
      redirect: '/onboarding/owner',
      emailMismatch,
      resumeEligible: false,
      missingProfileFields: [],
    };
  }
  if (me.state === 'complete') {
    return {
      mode: 'redirect',
      serverEmail,
      redirect: '/dashboard/home',
      emailMismatch,
      resumeEligible: false,
      missingProfileFields: [],
    };
  }

  // STRICT resume gate (blocker C): all three must hold.
  const resumeEligible =
    me.resume_eligible === true &&
    me.state === 'partial' &&
    Boolean(serverEmail);

  if (resumeEligible) {
    if (me.profile_recovery?.eligible === true) {
      if (missingProfileFields.length > 0) {
        return {
          mode: 'profile_recovery',
          serverEmail,
          redirect: null,
          emailMismatch,
          resumeEligible: true,
          missingProfileFields,
        };
      }
      return {
        mode: 'block',
        serverEmail: null,
        redirect: '/login',
        emailMismatch,
        resumeEligible: false,
        missingProfileFields: [],
      };
    }
    return {
      mode: 'resume',
      serverEmail,
      redirect: null,
      emailMismatch,
      resumeEligible: true,
      missingProfileFields: [],
    };
  }

  // Anything else with a present session payload is fail-closed: block to login.
  // Never treat partial-without-email or unknown state as a fresh signup while
  // a token exists — that would replay registration under a stale session.
  return {
    mode: 'block',
    serverEmail: null,
    redirect: '/login',
    emailMismatch,
    resumeEligible: false,
    missingProfileFields: [],
  };
}
